import classNames from 'classnames'
import { addDays } from 'date-fns'
import { NextPageContext } from 'next'
import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import Cookies from 'universal-cookie'
import BasicHead from '../../../../../../components/common/BasicHead'
import ContentsLoginBanner, {
  APP_DOWN_CONTENTS_BANNER_LOCATION,
} from '../../../../../../components/common/ContentsLoginBanner'
import MobileAppDownPopup, { APP_DOWN_POPUP_LOCATION } from '../../../../../../components/common/MobileAppDownPopup'
import LandingNav from '../../../../../../components/landing/navigation/LandingNav'
import MobileLandingNav from '../../../../../../components/landing/navigation/LandingNav/MobileLandingNav'
import { MediaPanel, QuestionPanel } from '../../../../../../components/modal/MaterialModal'
import ExpressionPanel from '../../../../../../components/new-portal/materials/ExpressionPanel'
import MaterialDesktopBottomTab from '../../../../../../components/new-portal/materials/MaterialDesktopBottomTab'
import MaterialInsightBody from '../../../../../../components/new-portal/materials/MaterialInsightBody'
import MaterialMobileBottomTab from '../../../../../../components/new-portal/materials/MaterialMobileBottomTab'
import { useGTMBasicPageViewEventWithProperties } from '../../../../../../helpers/gtmHelper'
import apis from '../../../../../../modules/apis'
import { axiosGet } from '../../../../../../modules/axiosHelper'
import { useCurrentUser } from '../../../../../../modules/context/UserContext'
import { COOKIE_DOMAIN, RINGLE_DOMAIN } from '../../../../../../modules/envVars'
import useAppRouter from '../../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../../modules/hooks/common/useIsMobile'
import { LOCALE_KO } from '../../../../../../modules/i18n/config'
import useTranslation from '../../../../../../modules/i18n/useTranslation'
import { STUDENT_PAGE_VIEW_ID } from '../../../../../../modules/ids/gtm/student'
import urls from '../../../../../../modules/urls'
import { COOKIE_OPT } from '../../../../../../modules/vars'
import { TextProvider } from '../../../../portal/materials/detail/[materialId]'

interface ILandingMaterialBodyComponent {
  activeTabIdx
  materialData
  materialId
}

const NEW_TAB = {
  INSIGHT: 0,
  QUESTIONS: 2,
  MEDIA: 3,
  EXPRESSIONS: 1,
}

const LandingMaterialBodyComponent = ({ activeTabIdx, materialData, materialId }: ILandingMaterialBodyComponent) => {
  const isMobile = useIsMobile()
  const { currentUser } = useCurrentUser()
  const media = {
    youtubes: materialData?.course?.video,
    articles: materialData?.course?.article,
  }
  const noLoginNoShowContentsClassName = !currentUser && 'max-h-[40vh] overflow-hidden'

  switch (activeTabIdx) {
    case NEW_TAB.INSIGHT:
      return <MaterialInsightBody materialData={materialData} materialId={materialId} isModal={true} />
    case NEW_TAB.EXPRESSIONS:
      return (
        <div className={noLoginNoShowContentsClassName}>
          <ExpressionPanel data={materialData} isShort={false} />
        </div>
      )
    case NEW_TAB.QUESTIONS:
      return (
        <div className={classNames(isMobile && 'px-[16px]', noLoginNoShowContentsClassName, 'pb-[60px]')}>
          <QuestionPanel questions={materialData?.keyquestions} isShort={false} />
        </div>
      )
    case NEW_TAB.MEDIA:
      return <MediaPanel media={media} materialId={materialId} className="!sm:px-[0px] !px-[16px]" />
    default:
      return <MaterialInsightBody materialData={materialData} materialId={materialId} isModal={true} />
  }
}

type MaterialDetailPropsType = {
  course: any
  [key: string]: any
  ogData: any
}

export const MaterialDetail = (props: MaterialDetailPropsType) => {
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const isMobile = useIsMobile()
  const { locale } = useTranslation()
  const cookies = new Cookies()
  const materialId = router.query.materialId
  const APP_DOWNLOAD_COOKIE = 'show_app_download_modal'

  const { ogData, materialData } = props

  const [showMobileAppDownPopup, setShowMobileAppDownPopup] = useState(true)
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)
  const [cookie, setCookie] = useCookies(['referralCode'])

  useEffect(() => {
    if (router.query.referralCode) {
      setCookie('referralCode', router.query.referralCode, COOKIE_OPT)
    }
  }, [router.query])

  const noShowAppDownloadModal = cookies.get(APP_DOWNLOAD_COOKIE) === 'close'

  const sendGTMPVEvent = useGTMBasicPageViewEventWithProperties({
    page_id: STUDENT_PAGE_VIEW_ID.LANDING_MATERIAL_DETAIL,
    page_title: router.asPath,
    device_type: isMobile ? 'mobile_web' : 'pc_web',
  })

  useEffect(() => {
    sendGTMPVEvent()

    window?.ChannelIO('hideChannelButton')
    setTimeout(() => window?.ChannelIO('hideChannelButton'), 1000)
    return () => window?.ChannelIO('showChannelButton')
  }, [])

  useEffect(() => {
    if (router.query.tab) {
      setActiveTabIdx(Number(router.query.tab))
    }
  }, [])

  const handleCloseAppModal = () => {
    setShowMobileAppDownPopup(false)
    cookies.set(APP_DOWNLOAD_COOKIE, 'close', {
      domain: COOKIE_DOMAIN,
      path: '/',
      expires: addDays(new Date(), 1),
    })
  }

  const handleSetActiveTabIdx = (tabIndex) => {
    setActiveTabIdx(tabIndex)
    router.replace(
      `/${locale}${urls.student.landing.contents.material.detail(materialId)}?tab=${tabIndex}`,
      undefined,
      { shallow: true }
    )
  }
  console.log(!noShowAppDownloadModal && Boolean(currentUser) && isMobile && showMobileAppDownPopup, 'asd')
  return (
    <div className="relative flex flex-col">
      <BasicHead ogData={ogData} noIndex />
      <div className="hidden lg:block">
        <LandingNav />
      </div>
      <div className="block lg:hidden">
        <MobileLandingNav />
      </div>
      <TextProvider>
        <div className="relative mx-auto flex h-full w-full max-w-[1040px] flex-col">
          <div className="relative mt-[40px] lg:max-w-[688px]">
            <LandingMaterialBodyComponent
              activeTabIdx={activeTabIdx}
              materialData={materialData}
              materialId={materialId}
            />
          </div>
          {!currentUser && (activeTabIdx == 1 || activeTabIdx == 2) && (
            <div className="absolute top-[-150px] mx-auto flex w-full justify-center">
              <ContentsLoginBanner
                bannerLocation={APP_DOWN_CONTENTS_BANNER_LOCATION.LANDING_MATERIAL}
                contentsId={materialId}
                isMobile={isMobile}
                loginAfterLink={urls.student.landing.contents.material.detail(materialId) + `?tab=${activeTabIdx}`}
              />
            </div>
          )}
          {activeTabIdx === 0 && (
            <>
              <div className="block sm:hidden">
                <MaterialMobileBottomTab course={materialData?.course} isLanding={true} />
              </div>
              <div className="z-[10] hidden flex-row sm:flex">
                <MaterialDesktopBottomTab course={materialData?.course} className="!pl-[0px]" isLanding={true} />
              </div>
            </>
          )}

          {!noShowAppDownloadModal && currentUser && isMobile && showMobileAppDownPopup && (
            <MobileAppDownPopup popupLocation={APP_DOWN_POPUP_LOCATION.LANDING_HOME} onClose={handleCloseAppModal} />
          )}
        </div>
      </TextProvider>
    </div>
  )
}

MaterialDetail.getInitialProps = async (context: NextPageContext) => {
  const { materialId, lang = LOCALE_KO } = context.query

  const json = await axiosGet(apis.course.detail({ id: materialId }))

  let userAgent: string

  if (context.req) {
    // if you are on the server and you get a 'req' property from your context
    userAgent = context.req.headers['user-agent'] // get the user-agent from the headers
  } else {
    userAgent = navigator.userAgent // if you are on the client you can access the navigator from the window object
  }

  const materialOgData = {
    title: lang == LOCALE_KO ? json.course?.title_kr : json.course?.title_en,
    desc: lang == LOCALE_KO ? json.course?.subtitle_kr : json.course?.subtitle_en,
    image: json.course?.image_long_url,
    ogUrl: `https://${RINGLE_DOMAIN}/${lang}/student/landing/contents/material/${materialId}`,
  }

  return {
    materialData: json,
    ogData: materialOgData,
  }
}

export default MaterialDetail
